import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { differenceWith } from "lodash";

import { API, graphqlOperation, Storage } from "aws-amplify";
import { createPhoto, deletePhoto } from "../../graphql/mutations";
// import { onCreatePhoto, onDeletePhoto } from "../../graphql/subscriptions";
// import { photoByParentByTime } from "../../graphql/queries";

import { Box, Dialog, makeStyles } from "@material-ui/core";

import { ImageInput, useNotify, useRecordContext } from "react-admin";
import { useForm } from "react-final-form";

import { logError } from "../../utils/appUtils";
import { getDirFileKey, isSameFile } from "../../utils/fileUtils";
import {
  MAX_PHOTO_SIZE,
  MAX_PHOTO_SIZE_MSG,
  PhotoStatus,
  ResourceType,
} from "../../utils/constants";
import { AttachPhotoIcon } from "./iconography";
import { ImageField } from "./ImageField";

const useStyles = makeStyles((theme) => ({
  mediaFull: {
    margin: "0 auto 0",
    width: "auto",
    maxWidth: "100%",
    height: "auto",
    borderRadius: "6px",
    boxShadow: "0 10px 20px rgba(0, 0, 0, 0.19), 0 6px 6px rgba(0, 0, 0, 0.23)",
  },
}));

const PhotoInput = ({
  source,
  label = "PHOTOS",
  parentType = ResourceType.INVENTORY,
  storeDir,
  maxFiles = 5,
  thumbs = 6,
  disabled,
  photos,
  setPhotos,
  ...props
}) => {
  const classes = useStyles();
  const form = useForm();
  const location = useLocation(); // used to force refresh photos on every visit.
  const notify = useNotify();
  const gridRef = useRef(null);
  const record = useRecordContext();
  const [thumbWidth, setThumbWidth] = useState(64);
  const [magnifiedUrl, setMagnifiedUrl] = useState(null);

  useEffect(() => {
    if (gridRef.current) {
      const imageMargin = 8; // defined in ImageField.classes.image
      const containerWidth = gridRef.current.offsetWidth;
      const size = Math.floor(
        (containerWidth - thumbs * 2 * imageMargin) / thumbs,
      );
      setThumbWidth(size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridRef.current, location.key, thumbs]);

  const handleImageChange = async (value = []) => {
    if (!Array.isArray(value)) return;

    const existingPhotos = photos || [];
    const formPhotos = form.getFieldState(source)?.value || [];
    let filteredPhotos = formPhotos;
    debugger;

    if (value.length > existingPhotos.length) {
      filteredPhotos = validateAndFilterPhotos(
        formPhotos,
        maxFiles,
        MAX_PHOTO_SIZE,
        notify,
      );

      form.change(source, filteredPhotos);
    }

    const newPhotos = filteredPhotos.filter((p) => p.rawFile);
    const removedPhotos = differenceWith(existingPhotos, value, isSameFile);

    if (newPhotos.length > 0) {
      const updatedPhotos = filteredPhotos.map((photo) =>
        photo.rawFile ? { ...photo, status: PhotoStatus.UPLOADING } : photo,
      );
      form.change(source, updatedPhotos);
      setPhotos(updatedPhotos);

      const createdPhotos = await handleCreatePhotos(
        newPhotos,
        record,
        parentType,
        storeDir,
        notify,
      );
      const finalPhotos = [
        ...updatedPhotos.filter((p) => !p.rawFile), // keep non-rawFile ones
        ...createdPhotos, // replace rawFile entries with final uploaded ones
      ];
      form.change(source, finalPhotos);
      setPhotos(finalPhotos);
    }
    for (const photo of removedPhotos) {
      await handleDeletePhoto(photo, notify);
      setPhotos(formPhotos);
    }
  };

  const handleMagnifyPhoto = (event) => {
    if (!magnifiedUrl) setMagnifiedUrl(event.target.lowsrc);
    else setMagnifiedUrl(null);
  };

  return (
    <Box
      ref={gridRef}
      width="100%"
      style={{
        pointerEvents: disabled ? "none" : "auto",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <ImageInput
        source={source}
        label={label}
        variant="outlined"
        accept="image/*"
        fullWidth
        multiple
        maxFiles={maxFiles}
        options={{ maxFiles }}
        placeholder={
          <p>
            <AttachPhotoIcon fontSize="large" /> <br /> Drop photos to upload,
            or click to select.
          </p>
        }
        onChange={handleImageChange}
      >
        <ImageField
          source="src"
          title="name"
          thumbWidth={thumbWidth}
          scaleThumb={true}
          fullSize={window.innerWidth}
          onClick={handleMagnifyPhoto}
        />
      </ImageInput>
      {magnifiedUrl ? (
        <Dialog open={magnifiedUrl} maxWidth="lg" onClick={handleMagnifyPhoto}>
          <img
            src={magnifiedUrl}
            alt="Inventory"
            className={classes.mediaFull}
            onClick={handleMagnifyPhoto}
          />
        </Dialog>
      ) : null}
    </Box>
  );
};

export const validateAndFilterPhotos = (photos, maxNum, maxSize, notify) => {
  const filtered = [];
  let sizeError = false;
  let countError = false;

  for (const photo of photos) {
    const size = photo?.size || photo?.rawFile?.size;
    if (size > maxSize) {
      sizeError = true;
      continue;
    }
    filtered.push(photo);
  }

  if (filtered.length > maxNum) {
    countError = true;
    filtered.length = maxNum; // Trim in-place
  }

  if (sizeError) notify(MAX_PHOTO_SIZE_MSG, "warning");
  if (countError) notify(`Max ${maxNum} photos allowed.`, "warning");

  return filtered;
};

const handleCreatePhotos = async (
  photosToCreate,
  record,
  parentType,
  dir,
  notify,
) => {
  let successCount = 0;
  const createdPhotos = [];
  const parentId = record?.id;
  const isEditMode = Boolean(parentId);

  for (const photo of photosToCreate) {
    try {
      const fileType = photo.rawFile?.type;
      const key = getDirFileKey(photo.rawFile, dir);

      await Storage.put(key, photo.rawFile, {
        level: "public",
        contentType: fileType,
      });

      const newPhotoInput = {
        key,
        bucket: API._options.aws_user_files_s3_bucket,
        region: API._options.aws_user_files_s3_bucket_region,
        name: photo.rawFile.name,
        type: fileType,
        reloadYardId: window.appUser.reloadYardId,
        parentId: parentId,
        parentType: parentType,
        createdBy: window.appUser.fullName,
        owner: window.appUser.owner,
        groups: window.appUser.groups,
      };

      if (isEditMode) {
        const res = await API.graphql(
          graphqlOperation(createPhoto, {
            input: newPhotoInput,
          }),
        );
        const created = res.data?.createPhoto;
        if (created) {
          createdPhotos.push({ ...created });
        }
      } else {
        // In create mode, just return a local representation of the photo
        createdPhotos.push(newPhotoInput);
      }

      successCount++;
    } catch (err) {
      logError("PhotoInput:createPhoto", err, notify);
    }
  }

  if (successCount > 0) {
    notify(`${successCount} photo${successCount > 1 ? "s" : ""} uploaded.`);
  }

  return createdPhotos;
};

const handleDeletePhoto = async (photo, notify) => {
  try {
    debugger;
    if (photo.id) {
      await API.graphql(
        graphqlOperation(deletePhoto, {
          input: { id: photo.id },
        }),
      );
    }
    if (photo.key) {
      await Storage.remove(photo.key);
    }
    notify("Photo deleted.");
  } catch (err) {
    logError("PhotoInput:deletePhoto", err, notify);
  }
};

export default PhotoInput;
